import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Breadcrumbs from '../../components/Breadcrumbs';
import SEO from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { ArrowRight, CheckCircle, Activity } from 'lucide-react';

const MedicalDevices = () => {
  return (
    <div>
      <SEO 
        title="Medical Device Trade Compliance & Export Controls"
        description="Navigate FDA regulations, EAR controls, and international medical device export requirements. Expert compliance for diagnostic equipment, surgical instruments, and healthcare technology."
        keywords="medical device export, healthcare trade compliance, medical equipment regulations, diagnostic device exports"
        path="/industries/medical-devices"
      />
      <Header />
      <Breadcrumbs items={[
        { label: 'Services', path: '/services' },
        { label: 'Medical Devices', path: '/industries/medical-devices' }
      ]} />
      
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-block h-1 w-16 bg-slate-600 mb-8"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Medical Device Trade Compliance
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              Medical device exports face unique challenges combining FDA requirements, export controls, and destination country regulations. We provide specialized compliance guidance for the healthcare technology sector.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Medical Device Export Considerations</h2>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <Activity className="h-8 w-8 text-blue-600 mb-3" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">EAR Classification</h3>
              <p className="text-sm text-gray-700">Diagnostic equipment, imaging systems, and advanced medical technology may be controlled under ECCN 3A292, 3A980, or other categories.</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">FDA Requirements</h3>
              <p className="text-sm text-gray-700">FDA export certificates, establishment registration, and compliance with device classification may be required for certain destinations.</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Destination Rules</h3>
              <p className="text-sm text-gray-700">Import permits, local registration, and destination country requirements vary significantly for medical devices.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Medical Device Compliance Services</h2>
          
          <div className="max-w-4xl mx-auto space-y-4">
            {[
              'Medical device ECCN classification',
              'FDA export certificate assistance',
              'EAR licensing for controlled medical equipment',
              'End-use and end-user screening for healthcare buyers',
              'CE marking and international standards compliance coordination',
              'Import permit support for destination countries'
            ].map((service, index) => (
              <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-800">{service}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-700">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Medical Device Export Consultation
          </h2>
          <p className="text-xl text-slate-100 mb-10 max-w-2xl mx-auto">
            Navigate the complexities of medical device export regulations with expert guidance.
          </p>
          <a href="https://calendly.com/nancy-wood-blackbird/0tg-trade-compliance-services" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="bg-white hover:bg-slate-100 text-slate-900 px-10 h-14 font-semibold">
              Schedule Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MedicalDevices;